import { motion } from "framer-motion";
import { Award, Bell, Zap, Box, ChartColumn, CheckCircle2, CircuitBoard, Cpu, Building2, Clock, Cloud, CodeXml, Coins, DoorOpen, Droplets, Eye, Factory, Flame, Folder, Fuel, Gauge, Settings, HeartPulse, Home, KeyRound, Leaf, Lock, Mic, Network, Smartphone, Plug, Radar, RefreshCw, Bot, Ruler, Send, ShieldCheck, Sun, Tag, Target, Thermometer, Wrench, User, Users, Wifi, CircleX, ArrowDown } from "lucide-react";

const ICONS = { award: Award, bell: Bell, bolt: Zap, box: Box, chart: ChartColumn, check: CheckCircle2, chip: CircuitBoard, cpu: Cpu, city: Building2, clock: Clock, cloud: Cloud, code: CodeXml, coins: Coins, door: DoorOpen, drop: Droplets, eye: Eye, factory: Factory, flame: Flame, folder: Folder, gas: Fuel, gauge: Gauge, gear: Settings, heart: HeartPulse, home: Home, key: KeyRound, leaf: Leaf, lock: Lock, mic: Mic, network: Network, phone: Smartphone, plug: Plug, range: Radar, refresh: RefreshCw, robot: Bot, ruler: Ruler, send: Send, shield: ShieldCheck, sun: Sun, tag: Tag, target: Target, thermometer: Thermometer, tool: Wrench, user: User, users: Users, wifi: Wifi, x: CircleX };
const TONES = ["#0F766E", "#1D4ED8", "#059669", "#0891B2", "#7C3AED", "#B45309"];
const pop = (i) => ({ initial: { opacity: 0, y: 14, scale: 0.96 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { delay: 0.15 + i * 0.12, duration: 0.45, ease: [0.22, 1, 0.36, 1] } });
const split = (s) => { const i = s.indexOf(":"); return i < 0 ? [s, ""] : [s.slice(0, i).trim(), s.slice(i + 1).trim()]; };

const Cards = ({ items }) => (
  <div className="grid grid-cols-2 gap-2.5">
    {items.map((it, i) => {
      const [icon, title, ...rest] = it.split(":");
      const I = ICONS[icon.trim()] || Box;
      return (
        <motion.div key={`${i}-${it}`} {...pop(i)} className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm">
          <I className="w-5 h-5" style={{ color: TONES[i % TONES.length] }} />
          <div className="mt-1.5 text-[13px] font-bold text-[#0B1F3A] leading-tight">{title}</div>
          {rest.length > 0 && <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{rest.join(":")}</div>}
        </motion.div>
      );
    })}
  </div>
);

const Flow = ({ items }) => (
  <div className="flex flex-col items-stretch gap-1.5">
    {items.map((it, i) => (
      <motion.div key={`${i}-${it}`} {...pop(i)} className="flex flex-col items-center">
        <div className="w-full rounded-xl px-3 py-2 text-[13px] font-bold text-center text-white" style={{ background: TONES[i % TONES.length] }}>{it}</div>
        {i < items.length - 1 && <ArrowDown className="w-4 h-4 text-slate-400 mt-1" />}
      </motion.div>
    ))}
  </div>
);

const Ring = ({ items, center, loop }) => {
  const R = 38;
  if (crowded(items, 5, 34)) return <div className="space-y-3">{center && <div className="inline-block rounded-full bg-[#0B1F3A] text-white px-4 py-1.5 text-[12px] font-bold">{center}</div>}<Stack items={items} /></div>;
  return (
    <div className="relative w-full aspect-square max-w-[300px] mx-auto">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        <circle cx="50" cy="50" r={R} fill="none" stroke="#CBD5E1" strokeWidth="0.6" strokeDasharray={loop ? "0" : "2 2"} />
        {loop && <motion.circle cx="50" cy="50" r={R} fill="none" stroke="#0F766E" strokeWidth="1.2" strokeDasharray="10 229" animate={{ strokeDashoffset: [0, -239] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} />}
        {!loop && items.map((_, i) => { const a = (i / items.length) * 2 * Math.PI - Math.PI / 2; return <line key={i} x1="50" y1="50" x2={50 + R * Math.cos(a)} y2={50 + R * Math.sin(a)} stroke="#94A3B8" strokeWidth="0.5" />; })}
      </svg>
      {center && <motion.div {...pop(0)} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[34%] aspect-square rounded-full bg-[#0B1F3A] text-white grid place-items-center text-center text-[12px] font-bold p-2 shadow-lg">{center}</motion.div>}
      {items.map((it, i) => {
        const a = (i / items.length) * 2 * Math.PI - Math.PI / 2;
        const [t, d] = split(it);
        return (
          <motion.div key={`${i}-${it}`} {...pop(i + 1)} className="absolute -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white border border-slate-200 shadow-sm px-2 py-1 text-center w-max max-w-[36%] leading-tight"
            style={{ left: `${50 + R * Math.cos(a)}%`, top: `${50 + R * Math.sin(a)}%` }}>
            <div className="text-[12px] font-bold" style={{ color: TONES[i % TONES.length] }}>{t}</div>
            {d && <div className="text-[10px] text-slate-500 leading-tight">{d}</div>}
          </motion.div>
        );
      })}
    </div>
  );
};

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-2" data-testid="slide-visual-chips">
    {items.map((it, i) => <motion.span key={`${i}-${it}`} {...pop(i)} className="rounded-xl bg-white border-2 px-3 py-2 text-[12px] font-bold text-[#0B1F3A] leading-snug" style={{ borderColor: TONES[i % TONES.length] }}>{it}</motion.span>)}
  </div>
);
const crowded = (items, n, len) => items.length > n || items.some((x) => x.length > len);

const Mesh = ({ items }) => {
  if (crowded(items, 5, 22)) return <Chips items={items} />;
  const pts = items.map((_, i) => { const a = (i / items.length) * 2 * Math.PI; return [50 + 36 * Math.cos(a), 50 + 36 * Math.sin(a)]; });
  const links = pts.flatMap((p, i) => [[i, (i + 1) % pts.length], [i, (i + 2) % pts.length]]);
  return (
    <div className="relative w-full aspect-square max-w-[300px] mx-auto">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        {links.map(([a, b], k) => <motion.line key={k} x1={pts[a][0]} y1={pts[a][1]} x2={pts[b][0]} y2={pts[b][1]} stroke="#5EEAD4" strokeWidth="0.7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.2 + k * 0.05, duration: 0.5 }} />)}
      </svg>
      {items.map((it, i) => (
        <motion.span key={`${i}-${it}`} {...pop(i)} className="absolute -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white border-2 border-[#0F766E] px-2.5 py-1 text-[11px] leading-tight font-bold text-[#0B1F3A] text-center w-max max-w-[38%]" style={{ left: `${pts[i][0]}%`, top: `${pts[i][1]}%` }}>{it}</motion.span>
      ))}
    </div>
  );
};

const Vs = ({ items }) => (
  <div className="grid grid-cols-2 gap-2.5">
    {items.map((it, i) => {
      const [t, d] = split(it);
      return (
        <motion.div key={`${i}-${it}`} {...pop(i)} className={`rounded-xl p-3 ${i ? "bg-[#0B1F3A] text-white" : "bg-white border border-slate-200"}`}>
          <div className={`text-sm font-black ${i ? "text-[#5EEAD4]" : "text-slate-500"}`}>{t}</div>
          <ul className="mt-2 space-y-1.5">{d.split(";").map((x, j) => <li key={j} className="text-[12px] leading-snug flex gap-1.5"><span className={i ? "text-[#5EEAD4]" : "text-slate-400"}>•</span>{x.trim()}</li>)}</ul>
        </motion.div>
      );
    })}
  </div>
);

const Bars = ({ items }) => (
  <div className="space-y-3">
    {items.map((it, i) => {
      const [t, v] = split(it);
      return (
        <div key={`${i}-${it}`}>
          <div className="flex justify-between text-[12px] font-semibold text-[#0B1F3A]"><span>{t}</span><span>{v}%</span></div>
          <div className="mt-1 h-3 rounded-full bg-slate-100 overflow-hidden"><motion.div className="h-full rounded-full" style={{ background: TONES[i % TONES.length] }} initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ delay: 0.2 + i * 0.15, duration: 0.8 }} /></div>
        </div>
      );
    })}
  </div>
);

const Stack = ({ items }) => (
  <div className="space-y-1.5">
    {items.map((it, i) => {
      const [t, d] = split(it);
      return (
        <motion.div key={`${i}-${it}`} {...pop(i)} className="rounded-xl px-3 py-2 text-white" style={{ background: TONES[i % TONES.length], marginInline: `${i * 4}%` }}>
          <div className="text-[13px] font-black">{t}</div>{d && <div className="text-[11px] opacity-90 leading-snug">{d}</div>}
        </motion.div>
      );
    })}
  </div>
);

const Timeline = ({ items }) => (
  <ol className="relative border-l-2 border-[#99F6E4] ml-2 space-y-2.5">
    {items.map((it, i) => {
      const [t, d] = split(it);
      return (
        <motion.li key={`${i}-${it}`} {...pop(i)} className="pl-4 relative">
          <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#0F766E] ring-4 ring-[#F0FDFA]" />
          <div className="text-[13px] font-bold text-[#0B1F3A]">{t}</div>{d && <div className="text-[11px] text-slate-500">{d}</div>}
        </motion.li>
      );
    })}
  </ol>
);

const Code = ({ v }) => (
  <motion.pre {...pop(0)} className="rounded-xl bg-[#0B1F3A] text-[#A7F3D0] p-4 text-[11px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap break-words">
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{v.lang}</div>
    {(v.code || []).join("\n")}
  </motion.pre>
);

export const SlideVisual = ({ v }) => {
  if (!v) return null;
  const it = v.items || [];
  const map = {
    cards: () => <Cards items={it} />, flow: () => <Flow items={it} />, hub: () => <Ring center={it[0]} items={it.slice(1)} />, loop: () => <Ring items={it} loop />,
    mesh: () => <Mesh items={it} />, vs: () => <Vs items={it} />, bars: () => <Bars items={it} />, stack: () => <Stack items={it} />, timeline: () => <Timeline items={it} />, code: () => <Code v={v} />,
  };
  return <div data-testid={`slide-visual-${v.type}`}>{(map[v.type] || (() => <Flow items={it} />))()}</div>;
};

